import React, { useEffect, useState } from 'react';
import { FileUp } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/shared/ui/dialog';
import { useToast } from '@/shared/hooks/use-toast';
import { PDFFile, PDFFileList } from '../../api';
import { PDFUploadManager } from './PDFUploadManager';
import { PDFBatchUploadManager } from './PDFBatchUploadManager';
import { PDFUploadDropzone } from './PDFUploadDropzone';

interface UploadDrawingsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  projectId: number;
  initialFiles?: File[];
  // When supplied, the dialog shows a banner indicating the upload will be
  // treated as a new revision of this drawing (server auto-groups by
  // drawing identity / filename).
  revisionSource?: PDFFileList | null;
  onSingleUploadSuccess: (pdfFile: PDFFile, sourceFile: File) => void;
  onSingleUploadError: (error: Error) => void;
  onBatchComplete: () => void | Promise<void>;
}

const revisionLabel = (pdf: PDFFileList): string => {
  if (pdf.primary_drawing_number) {
    return pdf.primary_title
      ? `${pdf.primary_drawing_number} — ${pdf.primary_title}`
      : pdf.primary_drawing_number;
  }
  return pdf.primary_title || pdf.original_filename;
};

export const UploadDrawingsDialog: React.FC<UploadDrawingsDialogProps> = ({
  open,
  onOpenChange,
  projectId,
  initialFiles,
  revisionSource,
  onSingleUploadSuccess,
  onSingleUploadError,
  onBatchComplete,
}) => {
  const { toast } = useToast();
  const isRevisionFlow = Boolean(revisionSource);
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);

  useEffect(() => {
    if (!open) {
      setSelectedFiles([]);
      return;
    }
    if (initialFiles?.length) {
      setSelectedFiles(isRevisionFlow ? initialFiles.slice(0, 1) : initialFiles);
    }
  }, [initialFiles, isRevisionFlow, open]);

  const handleOpenChange = (nextOpen: boolean) => {
    if (!nextOpen) setSelectedFiles([]);
    onOpenChange(nextOpen);
  };

  const handleFilesSelected = (files: File[], rejected: File[]) => {
    if (rejected.length) {
      toast({
        title: 'Some files were skipped',
        description: `${rejected.length} non-PDF file(s) were ignored.`,
      });
    }
    if (files.length) setSelectedFiles(files);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle>{isRevisionFlow ? 'Upload Revision' : 'Upload Drawings'}</DialogTitle>
          <DialogDescription>
            {isRevisionFlow
              ? 'The new PDF will be linked as a new revision of the existing drawing once it finishes processing.'
              : 'Choose or drop PDFs. One file opens OCR-assisted review; multiple files upload as a batch.'}
          </DialogDescription>
        </DialogHeader>
        {isRevisionFlow && revisionSource && (
          <div className="flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900">
            <FileUp className="h-4 w-4 mt-0.5 flex-shrink-0" />
            <div className="min-w-0">
              <p className="font-medium">Revision of {revisionLabel(revisionSource)}</p>
              <p className="text-amber-800/80">
                Server auto-matches by sheet identity, so keep the same drawing number when you upload.
              </p>
            </div>
          </div>
        )}
        {selectedFiles.length === 0 && (
          <PDFUploadDropzone
            multiple={!isRevisionFlow}
            onFileSelect={(file) => setSelectedFiles([file])}
            onFilesSelect={handleFilesSelected}
            title={isRevisionFlow ? 'Drop a Revision PDF' : 'Drop Drawing PDFs'}
            description={isRevisionFlow
              ? 'Drop one PDF here, or browse to choose the revised drawing.'
              : 'Drop one PDF for review or several to start a batch upload automatically.'}
            buttonLabel={isRevisionFlow ? 'Choose Revision PDF' : 'Choose PDFs'}
            minHeightClassName="min-h-[300px]"
          />
        )}
        {selectedFiles.length === 1 && (
          <PDFUploadManager
            projectId={projectId}
            initialFile={selectedFiles[0]}
            onReset={() => setSelectedFiles([])}
            onUploadSuccess={(pdfFile, sourceFile) => {
              onSingleUploadSuccess(pdfFile, sourceFile);
              handleOpenChange(false);
            }}
            onUploadError={onSingleUploadError}
          />
        )}
        {selectedFiles.length > 1 && !isRevisionFlow && (
          <PDFBatchUploadManager
            projectId={projectId}
            initialFiles={selectedFiles}
            onReset={() => setSelectedFiles([])}
            onUploadComplete={async (batch) => {
              await onBatchComplete();
              if (batch.failed_files === 0) handleOpenChange(false);
            }}
          />
        )}
      </DialogContent>
    </Dialog>
  );
};
